import { useEffect, useRef, useState } from 'react';

/**
 * Returns a derived loading flag that stays `true` for at least `minDurationMs`
 * milliseconds after the real loading state transitions from `true` to `false`.
 *
 * ## Why this exists
 *
 * Fast network responses can cause `isLoading` to flip `true → false` within a
 * single React render batch, so the loading indicator never paints at all.  This
 * hook guarantees the indicator is visible for at least one full animation cycle,
 * giving users clear visual feedback regardless of response time.
 *
 * ## Behaviour
 *
 * - When `isLoading` becomes `true`: the returned flag immediately becomes `true`.
 * - When `isLoading` becomes `false`: the returned flag stays `true` for the
 *   remaining duration of `minDurationMs`, then flips to `false`.
 * - If loading restarts before the timer fires, the timer is cancelled and the
 *   flag remains `true` for the new loading cycle.
 *
 * @param isLoading   The raw loading boolean from the data store.
 * @param minDurationMs  Minimum visible duration in milliseconds (default 1500 —
 *   one full cycle of the `eag-grid-loading-slide` keyframe animation).
 */
export function useMinimumLoadingDuration(isLoading: boolean, minDurationMs: number = 1500): boolean {
  const [showLoading, setShowLoading] = useState(isLoading);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Minimum loading duration timer.
   * When isLoading becomes true: show loading indicator immediately.
   * When isLoading becomes false: keep showing for at least minDurationMs.
   *
   * The synchronous setShowLoading(true) triggers the lint rule, but this is
   * intentional — we need immediate visual feedback when loading starts.
   * The deferred setShowLoading(false) via setTimeout is inherently side-effectful.
   * Neither can be expressed as derived state.
   */
  // eslint-disable-next-line no-restricted-syntax -- intentional: timer with cleanup; defers loading indicator hide by minDurationMs; clearTimeout cleanup required when isLoading restarts before timer fires; synchronous setShowLoading(true) in effect is intentional (react-hooks/set-state-in-effect already suppressed)
  useEffect(() => {
    if (isLoading) {
      // Cancel any pending hide timer — loading restarted.
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      // eslint-disable-next-line react-hooks/set-state-in-effect -- immediate visual feedback for loading state; cannot derive from props
      setShowLoading(true);
    } else {
      // Delay hiding so the animation always completes at least one full cycle.
      timerRef.current = setTimeout(() => {
        setShowLoading(false);
        timerRef.current = null;
      }, minDurationMs);
    }
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isLoading, minDurationMs]);
  return showLoading;
}